import React from "react";
import PropTypes from "prop-types";
import { ThumbnailGallery, CarouselImage, ImageText } from "../../Composite";
import { Diagram } from '../../Molecules';
import {
  Highlight,
  IconBullets,
  Hero,
  Headline,
  Teaser,
  StandaloneCta,
  Logos,
  FAQ,
  ToolsShowcase,
  Video,
  CodeBlock,
  ManualTable,
  Spreadsheet,
} from "../../Composite";
import { Embed, Paragraph } from "../../Atoms";
import { Quiz, FictionaryItem } from "../../Apps";
import RawHtml from "../RawHtml";
import Markdown from "../Markdown";

const SECTION_BLOCKS = new Set([
  "thumbnail_gallery",
  "image_text",
  "carousel_image",
  "highlight",
  "icon_bullets",
  "hero",
  "headline",
  "teaser",
  "standalone_cta",
  "FAQ",
  "video",
  "logos",
  "tools_showcase",
  "quiz",
  "manual_table",
  "spreadsheet",
  "item",
  "diagram",
]);

const PROSE_BLOCKS = new Set([
  "h1",
  "h2",
  "paragraph",
  "markdown",
  "code_block",
  "code",
  "embed",
]);

// footnoteIndex: { [uuid]: number } — built once in SectionContentPage from
// the page-level `footnotes` array, forwarded only to RawHtml (the only
// place inline <footnote> tags get parsed). See decision-log-footnotes.md.
const StreamField = ({ streamField = [], pageType, footnoteIndex = {} }) => {
  if (!streamField || streamField.length === 0) return null;

  const renderBlock = (field) => {
    if (!field?.value && field?.value !== "") return null;

    switch (field.type) {
      case "h1":
        return <h1 className="title-1">{field.value}</h1>;
      case "h2":
        return <h2 className="title-2">{field.value}</h2>;
      case "paragraph":
        return <Paragraph html={field.value} footnoteIndex={footnoteIndex} />;
      case "thumbnail_gallery":
        return <ThumbnailGallery images={field.value} />;
      case "image_text":
        return <ImageText value={field.value} />;
      case "carousel_image":
        return (
          <CarouselImage
            value={field.value?.slides || []}
            heading={field.value?.heading}
            subtitle={field.value?.sub_heading}
          />
        );
      case "highlight":
        return <Highlight value={field.value} />;
      case "icon_bullets":
        return <IconBullets value={field.value} />;
      case "hero":
        return <Hero value={field.value} />;
      case "headline":
        return <Headline value={field.value} />;
      case "teaser":
        return <Teaser value={field.value} />;
      case "standalone_cta":
        return <StandaloneCta value={field.value} />;
      case "FAQ":
        return <FAQ value={field.value} />;
      case "video":
        return <Video value={field.value} />;
      case "code_block":
      case "code":
        return <CodeBlock value={field.value} />;
      case "markdown":
        return <Markdown value={field.value} />;
      case "logos":
        return (
          <Logos
            items={
              Array.isArray(field.value)
                ? field.value
                : field.value?.logos || field.value?.items || []
            }
            heading={field.value?.heading}
          />
        );
      case "embed":
        return <Embed value={field.value} />;
      case "item":
        return <FictionaryItem value={field.value} />;
      case "tools_showcase":
        return <ToolsShowcase value={field.value} />;
      case "quiz":
        return <Quiz value={field.value} />;
      case "manual_table":
        return <ManualTable value={field.value} />;
      case "spreadsheet":
        return <Spreadsheet value={field.value} />;
      case "diagram":
        return <Diagram value={field.value} />;
      default:
        return null;
    }
  };

  return (
    <>
      {streamField.map((field, index) => {
        const block = renderBlock(field);
        if (!block) return null;

        if (SECTION_BLOCKS.has(field.type)) {
          return (
            <React.Fragment key={`${field.type}-${index}`}>
              {block}
            </React.Fragment>
          );
        }

        if (PROSE_BLOCKS.has(field.type)) {
          return (
            <section
              key={`${field.type}-${index}`}
              className="section"
            >
              <div className="section__inner">
                <div className="section__body">{block}</div>
              </div>
            </section>
          );
        }

        return (
          <React.Fragment key={`${field.type}-${index}`}>
            {block}
          </React.Fragment>
        );
      })}
    </>
  );
};

StreamField.propTypes = {
  streamField: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      value: PropTypes.any,
    })
  ).isRequired,
  pageType: PropTypes.string,
  footnoteIndex: PropTypes.object,
};

export default StreamField;